import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Observable, from, Subject, Subscription } from 'rxjs';
import { map, switchMap, concatMap, debounceTime } from 'rxjs/operators';

import { LazyLoadEvent, MenuItem, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { SalesLead, SalesLeadItem } from 'src/app/shared/sales-leads'

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { ClientApiService } from 'src/app/services/client-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';
import { SalesApiService } from 'src/app/services/sales-api.service';
import { ProductApiService } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';

/** Column descriptor for the sales-leads PrimeNG table. */
export class column {
  field = '';
  header = '';
  nonhide: boolean;
  show: boolean;
  filter: boolean;
};

@Component({
  selector: 'app-sales-leads',
  templateUrl: './sales-leads.component.html',
  styleUrls: ['./sales-leads.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Sales leads (deals / projects) list page.
 *
 * Driven by two Subjects:
 * - `clients$` — full reload with `debounceTime(300)` → `switchMap(getSalesLeads)`.
 *   Results are filtered client-side to exclude entries older than 2004-04-09.
 * - `cliPage$` — lazy page slice emitted by PrimeNG's `onLazyLoad`.
 *
 * Five filter fields (`cf01`–`cf05`) and column visibility (`showRowOptions`) are both
 * persisted to `localStorage` on `ngOnDestroy`.
 *
 * Inline delete is guarded by a confirm dialog (`confirmDeleteDocumentOn`).
 */
export class SalesLeadsComponent implements OnInit, OnDestroy {

  private globalSettinsData: GlobalSettingsData;

  salesLeads: SalesLead[];

  datasource: SalesLead[];
  salesLead: SalesLead;

  clientSubsQuery: Subscription;
  cliPageQuery: Subscription;
  deleteSlQuery: Subscription;

  totalRecords: number;

  rows: number = 25;

  loading: boolean = true;

  private storeNameFilterOptions: string = 'salesleads-filters-1';

  cf01 = '';
  cf02 = '';
  cf03 = '';
  cf04 = '';
  cf05 = '';

  @ViewChild('dt') table: Table;

  search$: Observable<string>;
  private clients$ = new Subject<any>();
  private cliPage$ = new Subject<any>();

  private storeNameShowRowOptions: string = 'salesleads-columns-1';

  showPersRowsOptions = [
  //  { name: 'Реквизиты', value: 1 },
  //  { name: 'Банк (Счета)', value: 2 },
  //  { name: 'Банк (Адрес)', value: 16 },
  //  { name: 'Адрес', value: 4 },
  //  { name: 'Примечание', value: 8 },
  ];

  showRowOptions: number[] = [];

  confirmDeleteDocumentOn: boolean = false;

  confirmDeleteDocumentText: string;

  /** `true` when the sales-leads list is empty. */
  public get noItems(): boolean { return ((this.salesLeads || []).length < 1); }

  /** `true` when the "Note" column group is selected for display. */
  public get seeNote(): boolean { return this.showRowOptions.includes(8); }
  /** `true` when the "Codes" column group is selected for display. */
  public get seeCodes(): boolean { return this.showRowOptions.includes(1); }
  /** `true` when the "Banks" column group is selected for display. */
  public get seeBanks(): boolean { return this.showRowOptions.includes(2); }
  /** `true` when the "Bank address" column group is selected for display. */
  public get seeBAddr(): boolean { return this.showRowOptions.includes(16); }
  /** `true` when the "Address" column group is selected for display. */
  public get seeAddress(): boolean { return this.showRowOptions.includes(4); }

  constructor(
    private restapi: RestApiService,
    private prodApi: ProductApiService,
    private docApi: DocumentApiService,
    private router: Router,
    private salesApi: SalesApiService,
    private cliApi: ClientApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title
  ) { }

  /** Persists filter state and column visibility to localStorage, then unsubscribes all queries. */
  ngOnDestroy(): void {
    console.log('*** SalesLeadsComponent in ngOnDestroy');

    localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));
    localStorage.setItem(this.storeNameFilterOptions, JSON.stringify({
      cf01: this.cf01,
      cf02: this.cf02,
      cf03: this.cf03,
      cf04: this.cf04,
      cf05: this.cf05,
    }));

    if (this.clientSubsQuery) { this.clientSubsQuery.unsubscribe(); }

    if (this.cliPageQuery) { this.cliPageQuery.unsubscribe(); }

    if (this.deleteSlQuery) { this.deleteSlQuery.unsubscribe(); }

  }

  /** Wires both Subjects, restores filter/column state from localStorage, and triggers initial load. */
  ngOnInit(): void {
    console.log('*** SalesLeadsComponent in ngOnInit');

    let sDate: Date = new Date(2004, 4, 9);

    this.cliPageQuery =

      this.cliPage$.pipe(
        switchMap((opt: any) => {

          this.loading = true;
          return this.salesApi.getSalesLeads(opt).pipe(map(data => {

            this.salesLeads = data.filter(s => new Date(s.DOCREGCDATE) > sDate);
              this.loading = false;

            }));
        })).subscribe();

    this.clientSubsQuery =

      this.clients$.pipe(
        debounceTime(300),
        switchMap(options => {

          this.loading = true;
          return this.salesApi.getSalesLeads(options)
            .pipe(map(data => {

              let sls = data.filter(s => new Date(s.DOCREGCDATE) > sDate);

              this.totalRecords = sls.length;
              this.salesLeads = sls.slice(0, this.table ? this.table.rows : this.rows);

              this.loading = false;
            }));
        })).subscribe();

    let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
    if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }

    let _storedFilterOptions: string = localStorage.getItem(this.storeNameFilterOptions);
    if (_storedFilterOptions) {
      let sfo: any = JSON.parse(_storedFilterOptions);

      this.cf01 = sfo.cf01;
      this.cf02 = sfo.cf02;
      this.cf03 = sfo.cf03;
      this.cf04 = sfo.cf04;
      this.cf05 = sfo.cf05;
    }

    this.onReloadAllData(null);

    this.titleService.setTitle('Сделки (Sales Leads)');
  }

  /** PrimeNG `onLazyLoad` handler — pushes the requested page window to `cliPage$`. */
  loadClientList(event: LazyLoadEvent) {
    //in a real application, make a remote request to load data using state metadata from event
    //event.first = First row offset
    //event.rows = Number of rows per page
    //event.sortField = Field name to sort with
    //event.sortOrder = Sort order as number, 1 for asc and -1 for dec
    //filters: FilterMetadata object having field as key and filter value, filter matchMode as value

    console.log('*** SalesLeadsComponent Lazy Load initiated');
    console.log(event.filters);
    console.log(event.sortField);
    console.log(event.sortOrder);

    this.cliPage$.next({
      from: event.first,
      to: (event.first + event.rows),
      sp: {
        sf: event.sortField,
        so: event.sortOrder
      },
      fs: { ...event.filters }
    });
  }

  /** Triggers a full reload via `clients$` whenever a column filter value changes. */
  filterProdTable(event) {
    console.log('*** ProductListComponent filterProdTable: ' + JSON.stringify(event.filters));
    this.clients$.next({
      sp: {
        sf: this.table.sortField,
        so: this.table.sortOrder
      },
      fs: this.table.filters
    });
  }

  /** Navigates to the sales-lead editor for the selected entry. */
  viewSalesLeadEntry(cle: SalesLead) {
    console.log('*** SalesLeadsComponent viewCliListEntry: ' + JSON.stringify(cle));
    this.router.navigate(['/edit-sales-lead', cle.DOCREGID]);
  }

  /** Navigates to the sales-lead editor in new-lead mode (id 0). */
  onCreateNewSalesLead(event) {
    console.log('*** SalesLeadsComponent onCreateNewClient: ' + JSON.stringify(event))
    this.router.navigate(['/edit-sales-lead', 0]);
  }

  /** Deletes the confirmed sales lead and triggers a full list reload. */
  onDeleteDocumentConfirmation(event) {
    console.log('*** EditSalesLeadComponent onDeleteDocumentConfirmation: '/* + JSON.stringify(event)*/);

    this.deleteSlQuery =
      this.salesApi.deleteSalesLeadById(this.salesLead).subscribe(() => {
        console.log('*** EditSalesLeadComponent onDeleteDocumentConfirmation: DONE!');
        this.onReloadAllData(null);
      });
  }

  /** Opens the delete confirmation dialog for the currently selected sales lead. */
  deleteSalesLead(event) {
    console.log('*** EditSalesLeadComponent deleteDocumentClick: ' /*+ JSON.stringify(event)*/);

    this.confirmDeleteDocumentText = 'DELETE ' + this.salesLead.DOCREGID;
    this.confirmDeleteDocumentOn = true;
  }

  /** Clears all table filters, resets filter fields, and triggers a full reload. */
  clearOrResetTableFilter(event) {
    console.log('*** SalesLeadsComponent clearOrResetTableFilter: ' + JSON.stringify(event));

    this.table.clearState();
    this.table.clear();
    this.table.reset();

    this.table.filters = {};

    this.cf01 = '';
    this.cf02 = '';
    this.cf03 = '';
    this.cf04 = '';
    this.cf05 = '';

    this.clients$.next({});
  }

  /** Forces a full reload while preserving the current sort and filter state. */
  onReloadAllData(event) {
    console.log('*** SalesLeadsComponent onReloadAllData: ' + JSON.stringify(event));

    let getoptions: any = {};

    if (this.table) {
      getoptions = {
        sp: {
          sf: this.table.sortField,
          so: this.table.sortOrder
        },
        fs: this.table.filters
      }
    }
    this.clients$.next(getoptions);
  }

  /** Handles a change in the row-display options multiselect (currently a no-op). */
  onRowsOptionChange(event) {
    //  console.log('*** SalesLeadsComponent in onRowsOptionChange: ' + JSON.stringify(event));
  }
}
